import { getFirstFourWords } from "./utils/strings";
import { isTeacherWithoutClass, isTeacher } from "./components/Header2/rules";
import { getUserRole } from "./userLocalDetails";

export const createResourceTitleBreadcrumb = (
  pathname,
  classInfo,
  hash = window.location.hash,
  submissionData,
  assignment,
  markingCriteria,
  rubricsId,
  completedFeedbackTasks,
  communityTasks,
  viewableSubmission,
  commentBanksData
) => {
  const role = getUserRole();

  if (pathname.includes('/documents/') || (hash && hash.includes('/documents/'))) {
    if (submissionData?.assignment?.title) {
      // A renamed draft goes by its name here as in the sidebar; the tooltip
      // still carries the question the title field holds.
      const renamed = submissionData.assignment.displayTitle?.trim();
      return [{
        name: renamed || submissionData.assignment.title,
        link: pathname,
        tooltip: submissionData.assignment.title,
        entityTooltip: true
      }];
    }
  }

  if (viewableSubmission) {
    return [{
      name: viewableSubmission?.assignment?.title,
      link: pathname,
      tooltip: viewableSubmission?.assignment.title,
      entityTooltip: true
    }]
  }
  if (hash === '#/tasks/new' || pathname === '/tasks/new') {
    return [
      {
        name: 'Create Task',
        link: '#/tasks/new',
        tooltip: 'Design a new task for your classes'
      }
    ];
  }

  const feedbackRequestMatch = pathname.match(/\/feedbackRequest\/documentsReview\/([^\/]+)/i);
  const feedbackHistoryMatch = pathname.match(/\/feedbackHistory\/documentsReview\/([^\/]+)/i);
  
  if (feedbackRequestMatch || feedbackHistoryMatch) {
    const itemId = feedbackRequestMatch ? feedbackRequestMatch[1] : feedbackHistoryMatch[1];
    const isFeedbackHistory = !!feedbackHistoryMatch;
    
    const baseNavItems = [];
    
    baseNavItems.push({
      name: isTeacher() ? "Tasks" : "School Work",
      link: "#/tasks",
      tooltip: isTeacher()
        ? 'View the status of every task that you have assigned to your classes'
        : 'View all of your current tasks from school'
    });
    
    baseNavItems.push({
      name: isFeedbackHistory
        ? (isTeacher() ? 'Feedback History' : 'Feedback From Me')
        : 'Feedback Requests',
      link: isFeedbackHistory ? '#/feedbackHistory' : '#/giveFeedback',
      tooltip: isFeedbackHistory
        ? 'View history of feedback provided'
        : (isTeacher()
            ? 'Provide personalized feedback'
            : 'Help other students who have requested feedback from the community')
    });

    const assignmentTitleFromSubmission = submissionData?.assignment?.title;
    
    if (!assignmentTitleFromSubmission) {
      const completedTasksList = completedFeedbackTasks?.content || (Array.isArray(completedFeedbackTasks) ? completedFeedbackTasks : []);
      const communityTasksList = communityTasks?.content || (Array.isArray(communityTasks) ? communityTasks : []);
      
      const feedbackTask = completedTasksList.find(task => task.id === itemId || task.submissionId === itemId);
      const communityTask = communityTasksList.find(task => task.id === itemId || task.submissionId === itemId);

      const assignmentTitle = feedbackTask?.assignment?.title || feedbackTask?.title ||
                             communityTask?.assignment?.title || communityTask?.title;
      
      if (assignmentTitle) {
        baseNavItems.push({
          name: assignmentTitle,
          link: pathname,
          tooltip: assignmentTitle,
          entityTooltip: true
        });
      }
    } else {
      baseNavItems.push({
        name: assignmentTitleFromSubmission,
        link: pathname,
        tooltip: assignmentTitleFromSubmission
      });
    }
    
    return baseNavItems;
  }

  if (pathname.match(/\/tasks\/[^\/]+/) && !pathname.includes('/tasks/new') && assignment) {

    if (assignment.title) {
      return [{
        name: assignment.title,
        link: pathname,
        tooltip: assignment.title
      }];
    } else {
      return [{
        name: "Task",
        link: pathname,
        tooltip: "View task details"
      }];
    }
  }

  if (pathname.includes('/view/submissions/') || hash.includes('/view/submissions/')) {
    if (submissionData?.assignment?.title) {
      return [{
        name: submissionData.assignment.title,
        link: pathname,
        tooltip: submissionData.assignment.title
      }];
    }
  }

  if (submissionData && pathname.includes('/submissions/')) {
    if (submissionData.assignment?.title) {
      return [{
        name: submissionData.assignment.title,
        link: pathname,
        tooltip: submissionData.assignment.title
      }];
    } 
  }

  if (rubricsId) {
    const criteriaId = rubricsId;
    return getMarkingCriteriaTitle(markingCriteria, criteriaId);
  }
  return [];
}

// Only fixed base-navigation labels are readable in replay. Entity titles are
// returned separately or have a dynamic path and never receive this marker.
const PUBLIC_CRUMB_PAIRS = new Set([
  'Tasks|#/tasks', 'School Work|#/tasks', 'Current Tasks|#/tasks',
  'Closed Tasks|#/completed', 'My Drafts|null',
  'Marking Tools|#/tools/rubrics', 'Rubrics|#/tools/rubrics',
  'Comment Banks|#/tools/commentbanks', 'Success Criteria|#/tools/successcriterias',
  'Short Answers|#/tools/shortanswers', 'Marking Preferences|#/tools/advanced-controls',
  'Class Insights|#/classes-insights', 'Feedback Requests|#/giveFeedback',
  'Help a Friend|#/giveFeedback', 'Feedback History|#/feedbackHistory',
  'Feedback From Me|#/feedbackHistory', 'Shared With Me|#/sharedtasks',
  'Shared with me|#/sharedmarkingtools', 'Exemplars|#/sharedresponses',
  'Model Responses|#/sharedresponses', 'Tasks|#/', 'Feedback From Me|#/',
]);
export const generateBreadcrumbs = ({
  pathname,
  classInfo,
  hash = window.location.hash,
  submissionData,
  assignment,
  markingCriteria,
  rubricsId,
  completedFeedbackTasks,
  communityTasks,
  viewableSubmission,
  commentBanksData,
  successCriteriaData,
  successCriteriaId,
  shortAnswersData
}) => {
  const role = getUserRole();
  const isExpert = isTeacherWithoutClass(role, classInfo);
  const homeTitle = isExpert ? 'Feedback From Me' : 'Tasks';
  const isClosedEntity = (assignment && assignment.status === 'CLOSED') || 
                        (submissionData && submissionData.status === 'CLOSED');
  
  const entityTitleBreadcrumb = createResourceTitleBreadcrumb(
    pathname,
    classInfo,
    hash,
    submissionData,
    assignment,
    markingCriteria,
    rubricsId,
    completedFeedbackTasks,
    communityTasks,
    viewableSubmission,
    commentBanksData
  );
  
  const effectivePath = (hash && hash.startsWith('#/') ? hash.substring(1) : pathname).split('?')[0].split('#')[0];
  const isDocumentsReview = effectivePath.includes('/feedbackRequest/documentsReview/') || effectivePath.match(/\/feedbackHistory\/documentsReview\//i);
  const hasParentBreadcrumb = entityTitleBreadcrumb && entityTitleBreadcrumb.length > 1;
  
  const baseBreadcrumbs = (isDocumentsReview && hasParentBreadcrumb)
    ? []
    : getBaseNavigation(pathname, hash, homeTitle, isClosedEntity, commentBanksData, successCriteriaData, successCriteriaId, markingCriteria, shortAnswersData);
    
  const publicBaseBreadcrumbs = baseBreadcrumbs.map((item) =>
    PUBLIC_CRUMB_PAIRS.has(`${item.name}|${item.link}`)
      ? { ...item, replayPublic: true }
      : item
  );
  return [...publicBaseBreadcrumbs, ...entityTitleBreadcrumb.map(item=>{
    return {
      ...item,
      name: getFirstFourWords(item.name)
    };
  })];
};

function getBaseNavigation(pathname, hash, homeTitle, isClosedEntity, commentBanksData, successCriteriaData, successCriteriaId, markingCriteria, shortAnswersData) {
  const effectivePath = (hash && hash.startsWith('#/') ? hash.substring(1) : pathname).split('?')[0].split('#')[0];

  if (effectivePath.includes('/feedbackRequest/documentsReview/') || effectivePath.match(/\/feedbackHistory\/documentsReview\//i)) {
    return [
      {
        name: isTeacher() ? "Tasks" : "School Work",
        link: "#/tasks",
        tooltip: isTeacher() 
          ? 'View the status of every task that you have assigned to your classes'
          : 'View all of your current tasks from school'
      }
    ];
  }

  if (
    effectivePath.includes('/documents/') ||
    effectivePath === '/documents' ||
    effectivePath.includes('/getFeedback')
  ) {
    return [
      {
        name: "My Drafts",
        link: null,
        tooltip: "View and create your own  drafts"
      }
    ];
  }

  if (effectivePath.includes('/view/submissions/')) {
    return [];
  }

  if (effectivePath.includes('/submissions/')) {
    return [
      {
        name: isTeacher() ? "Tasks" : "School Work",
        link: "#/tasks",
        tooltip: isTeacher() 
          ? 'View the status of every task that you have assigned to your classes'
          : 'View all of your current tasks from school'
      },
      {
        name: isClosedEntity ? "Closed Tasks" : "Current Tasks",
        link: isClosedEntity ? "#/completed" : "#/tasks",
        tooltip: isTeacher() 
          ? (isClosedEntity ? 'View closed tasks for your classes' : 'View active tasks for your classes')
          : (isClosedEntity ? 'View your closed tasks' : 'View your active tasks')
      }
    ];
  }

  if (effectivePath.match(/\/tasks\/[^\/]+/) && !effectivePath.includes('/tasks/new')) {
    return [
      {
        name: isTeacher() ? "Tasks" : "School Work",
        link: '#/tasks',
        tooltip: isTeacher() 
          ? 'View the status of every task that you have assigned to your classes'
          : 'View all of your current tasks from school'
      },
      {
        name: isClosedEntity ? "Closed Tasks" : "Current Tasks",
        link: isClosedEntity ? "#/completed" : "#/tasks",
        tooltip: isTeacher() 
          ? (isClosedEntity ? 'View closed tasks for your classes' : 'View active tasks for your classes')
          : (isClosedEntity ? 'View your closed tasks' : 'View your active tasks')
      }
    ];
  }

  if (effectivePath.startsWith('/tools/commentbanks')) {
    const baseBreadcrumbs = [
      {
        name: 'Marking Tools',
        link: '#/tools/rubrics',
        tooltip: isTeacher()
          ? 'A library of customisable marking tools that can be used for any task'
          : 'View marking tools for your assignments',
      },
      {
        name: 'Comment Banks',
        link: '#/tools/commentbanks',
        tooltip: 'Create and manage reusable comments for student feedback',
      },
    ];


    const commentBankMatch = effectivePath.match(/\/tools\/commentbanks\/([^\/#?]+)/);
    if (commentBankMatch) {
      const commentBankId = commentBankMatch[1]; 
      const commentBanks = commentBanksData?._embedded?.commentbanks || [];
      const commentBank = commentBanks.find(cb => cb.id === commentBankId);
      const commentBankTitle = commentBank?.title || 'Comment Bank';
      
      baseBreadcrumbs.push({
        name: commentBankTitle,
        link: effectivePath,
        tooltip: commentBankTitle,
      });
    }

    return baseBreadcrumbs;
  }
  if (effectivePath.startsWith('/tools/successcriterias')) {
    const baseBreadcrumbs = [
      {
        name: 'Marking Tools',
        link: '#/tools/rubrics',
        tooltip: isTeacher()
          ? 'A library of customisable marking tools that can be used for any task'
          : 'View marking tools for your assignments',
      },
      {
        name: 'Success Criteria',
        link: '#/tools/successcriterias',
        tooltip: 'Create and manage success criteria for student assessment',
      },
    ];

    const successCriteriaMatch = effectivePath.match(/\/tools\/successcriterias\/([^\/#?]+)/);
    if (successCriteriaMatch) {
      const matchedId = successCriteriaMatch[1];
      const successCriteriaIdToUse = successCriteriaId || matchedId;
      let successCriteriaList = [];
      if (Array.isArray(successCriteriaData)) {
        successCriteriaList = successCriteriaData;
      } else if (successCriteriaData?._embedded?.successCriteria) {
        successCriteriaList = successCriteriaData._embedded.successCriteria;
      }
      const successCriteria = successCriteriaList.find(sc => sc.id === successCriteriaIdToUse);
      const successCriteriaTitle = successCriteria?.title || 'Success Criteria';

      baseBreadcrumbs.push({
        name: successCriteriaTitle,
        link: effectivePath,
        tooltip: successCriteriaTitle,
      });
    }

    return baseBreadcrumbs;
  }
  if (effectivePath.startsWith('/tools/shortanswers')) {
    const baseBreadcrumbs = [
      {
        name: 'Marking Tools',
        link: '#/tools/rubrics',
        tooltip: isTeacher()
          ? 'A library of customisable marking tools that can be used for any task'
          : 'View marking tools for your assignments',
      },
      {
        name: 'Short Answers',
        link: '#/tools/shortanswers',
        tooltip: 'Create and manage short answer criteria for student assessment',
      },
    ];

    const shortAnswerMatch = effectivePath.match(/\/tools\/shortanswers\/([^\/#?]+)/);
    if (shortAnswerMatch) {
      const matchedId = shortAnswerMatch[1];
      let shortAnswersList = [];
      if (Array.isArray(shortAnswersData)) {
        shortAnswersList = shortAnswersData;
      } else if (shortAnswersData?._embedded?.shortAnswers) {
        shortAnswersList = shortAnswersData._embedded.shortAnswers;
      }
      const shortAnswer = shortAnswersList.find(sa => sa.id === matchedId);
      const shortAnswerTitle = shortAnswer?.title || 'Short Answer';

      baseBreadcrumbs.push({
        name: shortAnswerTitle,
        link: effectivePath,
        tooltip: shortAnswerTitle,
      });
    }

    return baseBreadcrumbs;
  }
  if (effectivePath.startsWith('/sharedmarkingtools')) {
    return [
      {
        name: 'Marking Tools',
        link: '#/tools/rubrics',
        tooltip: isTeacher()
          ? 'A library of customisable marking tools that can be used for any task'
          : 'View marking tools for your assignments',
      },
      {
        name: 'Shared with me',
        link: '#/sharedmarkingtools',
        tooltip: 'View marking tools that have been shared with you',
      },
    ];
  }
  if (effectivePath.startsWith('/jeddai')) {
    return [
      {
        name: 'Marking Tools',
        link: '#/tools/rubrics',
        tooltip: isTeacher()
          ? 'A library of customisable marking tools that can be used for any task'
          : 'View marking tools for your assignments',
      },
      {
        name: 'Marking Preferences',
        link: '#/tools/advanced-controls',
        tooltip: 'Adjust your marking preferences and JeddAI settings',
      },
    ];
  }
  if (effectivePath.startsWith('/tools/rubrics')) {
    const baseBreadcrumbs = [
      {
        name: 'Marking Tools',
        link: '#/tools/rubrics',
        tooltip: isTeacher()
          ? 'A library of customisable marking tools that can be used for any task'
          : 'View marking tools for your assignments',
      },
      {
        name: 'Rubrics',
        link: '#/tools/rubrics',
        tooltip: 'Browse all available rubrics',
      },
    ];

    const rubricMatch = effectivePath.match(/\/tools\/rubrics\/([^\/#?]+)/);
    if (rubricMatch) {
      const rubricId = rubricMatch[1];
      if (markingCriteria) {
        const rubric = Array.isArray(markingCriteria) 
          ? markingCriteria.find(mc => mc.id === rubricId)
          : markingCriteria;
        const rubricTitle = rubric?.title || 'Rubric';
        
        baseBreadcrumbs.push({
          name: rubricTitle,
          link: effectivePath,
          tooltip: rubricTitle,
        });
      }
    }

    return baseBreadcrumbs;
  }
  if (effectivePath.startsWith('/classes-insights') || effectivePath.startsWith('/classes')) {
    return [
      {
        name: 'Class Insights',
        link: '#/classes-insights',
        tooltip: isTeacher()
          ? 'View performance data and insights for your classes'
          : 'View class performance and insights',
      },
    ];
  }
  if (effectivePath.startsWith('/giveFeedback')) {
    return [
      {
        name: isTeacher() ? 'Tasks' : 'School Work',
        link: '#/tasks',
        tooltip: isTeacher()
          ? 'View the status of every task that you have assigned to your classes'
          : 'View all of your current tasks from school',
      },
      {
        name: isTeacher() ? 'Feedback Requests' : 'Help a Friend',
        link: '#/giveFeedback',
        tooltip: isTeacher()
          ? 'Provide personalized feedback'
          : 'Help other students who have requested feedback from the community',
      },
    ];
  }
  if (effectivePath.startsWith('/tasks')) {
    return [
      {
        name: isTeacher() ? 'Tasks' : 'School Work',
        link: '#/tasks',
        tooltip: isTeacher()
          ? 'View the status of every task that you have assigned to your classes'
          : 'View all of your current tasks from school',
      },
      {
        name: 'Current Tasks',
        link: '#/tasks',
        tooltip: isTeacher()
          ? 'View active tasks for your classes'
          : 'View your active tasks',
      },
    ];
  }
  if (effectivePath.startsWith('/feedbackHistory')) {
    return [
      {
        name: isTeacher() ? 'Tasks' : 'School Work',
        link: '#/tasks',
        tooltip: isTeacher()
          ? 'View the status of every task that you have assigned to your classes'
          : 'View all of your current tasks from school',
      },
      {
        name: isTeacher() ? 'Feedback History' : 'Feedback From Me',
        link: '#/feedbackHistory',
        tooltip: 'View history of feedback provided',
      },
    ];
  }
  if (effectivePath.startsWith('/settings')) {
    return [
      {
        name: 'Marking Tools',
        link: '#/tools/rubrics',
        tooltip: isTeacher()
          ? 'A library of customisable rubrics that can be used for any new task'
          : 'View rubrics for your assignments',
      },
      {
        name: 'Rubrics',
        link: '#/tools/rubrics',
        tooltip: 'Browse available rubrics',
      },
    ];
  }
  if (effectivePath.startsWith('/sharedtasks')) {
    return [
      {
        name: isTeacher() ? 'Tasks' : 'School Work',
        link: '#/tasks',
        tooltip: isTeacher()
          ? 'View the status of every task that you have assigned to your classes'
          : 'View all of your current tasks from school',
      },
      {
        name: 'Shared With Me',
        link: '#/sharedtasks',
        tooltip: 'View tasks that have been shared with you',
      },
    ];
  }
  if (effectivePath.startsWith('/sharedresponses')) {
    return [
      {
        name: isTeacher() ? 'Tasks' : 'School Work',
        link: '#/tasks',
        tooltip: isTeacher()
          ? 'View shared responses from students'
          : 'A collection of student work that has been shared with the class',
      },
      {
        name: isTeacher() ? 'Exemplars' : 'Model Responses',
        link: '#/sharedresponses',
        tooltip: 'Browse shared work examples',
      },
    ];
  }
  if (effectivePath.startsWith('/completed')) {
    return [
      {
        name: isTeacher() ? 'Tasks' : 'School Work',
        link: '#/tasks',
        tooltip: isTeacher()
          ? 'Consists of all the tasks that you have marked as closed'
          : 'View all of the tasks that are marked as closed',
      },
      {
        name: 'Closed Tasks',
        link: '#/completed',
        tooltip: 'View tasks that have been completed and closed',
      },
    ];
  }
  if (effectivePath.startsWith('/#') || effectivePath === '/' || effectivePath === '') {
    return [{
      name: homeTitle,
      link: '#/',
      tooltip: isTeacher() 
        ? 'View the status of every task that you have assigned to your classes'
        : 'View all of your current tasks from school'
    }];
  }
  return [];
}

function getMarkingCriteriaTitle(markingCriteria, criteriaId) {
  if (!markingCriteria || !criteriaId) return [];
  
  const criteria = markingCriteria.find(item => item.id === criteriaId);
  if (criteria?.title) {
    return [
      {
        name: criteria.title,
        link: null,
        tooltip: criteria.title
      }
    ];
  }
  
  return [];
}
